import type WorkflowSchema from '../../models/workflow-schema.js';
import type { TemplateSetting } from '../../models/workflow-library.js';
import type { Draft } from '../../ns-workflows/builder/simple.js';

// What a template question writes: a step's title, or one of its values
const SETTING_PATH = /^nodes\.([^.]+)\.(?:title|values\.(\w+))$/;

const placeOf = (path: string) => {
  const [, nodeId, input] = SETTING_PATH.exec(path) ?? [];
  return nodeId ? { nodeId, input } : undefined;
};

// What a template question holds now: the value at the first place it writes
export function readSetting(draft: Partial<Draft>, setting: TemplateSetting): unknown {
  const place = placeOf(setting.writes[0] ?? '');
  const node = draft.nodes?.find((n) => n.id === place?.nodeId);
  if (!place || !node) return undefined;
  return place.input ? node.values?.[place.input] : node.title;
}

// The draft with a question's answer written into every place it names
export function writeSetting(draft: Partial<Draft>, setting: TemplateSetting, value: unknown): Partial<Draft> {
  const places = setting.writes.flatMap((path) => placeOf(path) ?? []);
  return {
    ...draft,
    nodes: (draft.nodes ?? []).map((node) =>
      places
        .filter((place) => place.nodeId === node.id)
        .reduce(
          (written, { input }) =>
            input
              ? { ...written, values: { ...written.values, [input]: value } }
              : { ...written, title: String(value) },
          node,
        ),
    ),
  };
}

// The questions still worth asking: every place each writes is still in the draft
export function askableSettings(
  draft: Partial<Draft>,
  settings: Record<string, TemplateSetting> | undefined,
): [string, TemplateSetting][] {
  return Object.entries(settings ?? {}).filter(([, setting]) =>
    setting.writes.every((path) => {
      const place = placeOf(path);
      const node = draft.nodes?.find((n) => n.id === place?.nodeId);
      return !!place && !!node && (!place.input || place.input in (node.values ?? {}));
    }),
  );
}

const answered = (value: unknown) =>
  Array.isArray(value)
    ? value.length > 0
    : typeof value === 'object' && value !== null
      ? Object.keys(value).length > 0
      : typeof value === 'string' && value.trim() !== '';

// The labels of the required questions left unanswered
export function unansweredSettings(draft: Partial<Draft>, settings: [string, TemplateSetting][]): string[] {
  return settings
    .filter(([, setting]) => setting.required && !answered(readSetting(draft, setting)))
    .map(([, setting]) => setting.label);
}

// Role choices changed since the last publish, so the screen can say a publish is needed
export function unpublishedChanges(config: Record<string, unknown>, published: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(config), ...Object.keys(published)]);
  return [...keys].some((key) => JSON.stringify(config[key] ?? null) !== JSON.stringify(published[key] ?? null));
}

export interface LaunchPick {
  key: string;
  ref: 'people' | 'team' | 'dataRecord' | 'comRelationship';
  refSet?: string;
}

const PICKABLE_REFS = ['people', 'team', 'dataRecord', 'comRelationship'];
// Filled from the launch context, never picked
const CONTEXT_INPUTS = ['BASE_COMPANY_ID', 'COMPANY_ID', 'ASSIGNED_TO'];

// What the launcher picks before a company workflow starts: its entry inputs a binding left to launch
export function launchPicks(schema: Pick<WorkflowSchema, 'entry'>): LaunchPick[] {
  const inputs = (schema.entry?.launcher?.inputs ?? {}) as Record<string, unknown>;
  return Object.entries(inputs).flatMap(([key, declaration]) => {
    if (CONTEXT_INPUTS.includes(key) || typeof declaration !== 'object' || declaration === null) return [];
    const { ref, refSet } = declaration as { ref?: string; refSet?: string };
    return ref && PICKABLE_REFS.includes(ref)
      ? [{ key, ref: ref as LaunchPick['ref'], ...(refSet ? { refSet } : {}) }]
      : [];
  });
}

export const INVITEE_FIELDS = ['email', 'name', 'jobTitle', 'companyName'] as const;
export type InviteeField = (typeof INVITEE_FIELDS)[number];

// One invite step's details the starter gives: each invitee field's launch input
export interface InvitePrompt {
  nodeId: string;
  label: string;
  inputs: Partial<Record<InviteeField, string>>;
}

// The invite steps whose invitee comes from launch inputs, named after the company they invite
export function invitePrompts(schema: Pick<WorkflowSchema, 'entry' | 'nodes' | 'subjects'>): InvitePrompt[] {
  const inputs = (schema.entry?.launcher?.inputs ?? {}) as Record<string, unknown>;
  const subjects = Object.values((schema.subjects ?? {}) as Record<string, { via?: string; label?: string }>);
  return (schema.nodes ?? []).flatMap((node) => {
    if (node.type !== 'invite') return [];
    const invitee = (node.invitee ?? {}) as Record<string, unknown>;
    const asked = INVITEE_FIELDS.flatMap((field) => {
      const key = /^@env\.values\.(\w+)$/.exec(String(invitee[field] ?? ''))?.[1];
      return key && key in inputs ? [[field, key] as const] : [];
    });
    if (asked.length === 0) return [];
    const company = subjects.find((subject) => subject.via === node.relationship)?.label;
    return [{ nodeId: node.id, label: company ?? node.title ?? 'Who it invites', inputs: Object.fromEntries(asked) }];
  });
}

const INVITEE_EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
// The invitation email shows what was typed, so it can't carry a link, a bare domain a mail client would link, or markup
export const LINK_OR_MARKUP = /:\/\/|www\.|[<>]|[a-z0-9-]\.[a-z]{2,}(\/|\b)/i;
const MAX_INVITEE_TEXT = 100;
const MAX_INVITEE_EMAIL = 254;
const INVITEE_WHAT: Record<InviteeField, string> = {
  email: 'email',
  name: 'name',
  jobTitle: 'job title',
  companyName: 'company name',
};

// The lambdas' checks of an invitee's details at launch, so the start page can say what's wrong first
export function inviteeProblem(field: InviteeField, value: string): string | undefined {
  const text = value.trim();
  if (field === 'email') {
    return text.length <= MAX_INVITEE_EMAIL && INVITEE_EMAIL.test(text) ? undefined : 'Enter an email address.';
  }
  if (!text) return `Enter their ${INVITEE_WHAT[field]}.`;
  if (text.length > MAX_INVITEE_TEXT) return `Keep it to ${MAX_INVITEE_TEXT} characters.`;
  return LINK_OR_MARKUP.test(text) ? 'Leave out links, web addresses and < or >.' : undefined;
}

export const inviteeLabel = (field: InviteeField) =>
  INVITEE_WHAT[field].charAt(0).toUpperCase() + INVITEE_WHAT[field].slice(1);
